import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    GroupItemCategoryBreadcrumbs,
    GroupItemCategoryCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsGroupsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as GroupItemTemplateCategoryActions from "../../../actions/groupItemTemplateCategory";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { GroupSettingsLayout } from "../../index";

import "./GroupItemTemplateCategoryPage.scss";

interface GroupItemTemplateCategoryPageProps {
    form: Dtos.GroupItemTemplateCategory & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    groupItemTemplateCategory: Dtos.GroupItemTemplateCategory;
    lookups: Dtos.Lookup[];
    loadingGroupItemTemplateCategory: boolean;
    loadGroupItemTemplateCategoryFailure: boolean;
    loadGroupItemTemplateCategoryErrorCode: string;
    savingGroupItemTemplateCategory: boolean;
    saveGroupItemTemplateCategorySuccess: boolean;
    saveGroupItemTemplateCategoryFailure: boolean;
    creatingGroupItemTemplateCategory: boolean;
    createGroupItemTemplateCategorySuccess: boolean;
    validatingGroupItemTemplateCategory: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearGroupItemTemplateCategory: GroupItemTemplateCategoryActions.IGroupItemTemplateCategoryClearActionCreator;
    loadGroupItemTemplateCategory: GroupItemTemplateCategoryActions.IGroupItemTemplateCategoryLoadByIdActionCreator;
    saveGroupItemTemplateCategory: GroupItemTemplateCategoryActions.IGroupItemTemplateCategorySaveActionCreator;
    createGroupItemTemplateCategory: GroupItemTemplateCategoryActions.IGroupItemTemplateCategoryCreateActionCreator;
    validateGroupItemTemplateCategory: GroupItemTemplateCategoryActions.IGroupItemTemplateCategoryValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.groupItemTemplateCategory";

const GroupItemTemplateCategoryPage: FC<GroupItemTemplateCategoryPageProps> = ({
    form,
    formState,
    formProperties,
    groupItemTemplateCategory,
    lookups,
    loadingGroupItemTemplateCategory,
    loadGroupItemTemplateCategoryFailure,
    loadGroupItemTemplateCategoryErrorCode,
    savingGroupItemTemplateCategory,
    saveGroupItemTemplateCategorySuccess,
    saveGroupItemTemplateCategoryFailure,
    creatingGroupItemTemplateCategory,
    createGroupItemTemplateCategorySuccess,
    validatingGroupItemTemplateCategory,
    validationFailures,
    permissions,
    clearGroupItemTemplateCategory,
    loadGroupItemTemplateCategory,
    saveGroupItemTemplateCategory,
    createGroupItemTemplateCategory,
    validateGroupItemTemplateCategory,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { groupItemTemplateCategoryId } = useParams<{
        groupItemTemplateCategoryId: string;
    }>();
    const groupItemTemplateCategoryIdNumber = parseInt(
        groupItemTemplateCategoryId,
        10
    );

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.GroupItemTemplateCategory =
            {} as Dtos.GroupItemTemplateCategory;

        loadForm(
            reduxFormName,
            Object.assign({ ...groupItemTemplateCategory }, extraData)
        );
    };


    // Load trial category
    useEffect(() => {
        if (
            groupItemTemplateCategoryIdNumber &&
            !groupItemTemplateCategory &&
            !loadingGroupItemTemplateCategory
        ) {
            setDrawerItems(SettingsGroupsSubMenu(permissions));
            setBreadcrumbs(
                GroupItemCategoryBreadcrumbs(groupItemTemplateCategoryIdNumber)
            );
            loadGroupItemTemplateCategory(groupItemTemplateCategoryIdNumber);
        } else if (
            !groupItemTemplateCategoryIdNumber &&
            !creatingGroupItemTemplateCategory &&
            !createGroupItemTemplateCategorySuccess
        ) {
            createGroupItemTemplateCategory();
            setBreadcrumbs(GroupItemCategoryCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (groupItemTemplateCategory && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        groupItemTemplateCategoryIdNumber,
        groupItemTemplateCategory,
        setDrawerItems,
        SettingsGroupsSubMenu,
        permissions,
        setBreadcrumbs,
        GroupItemCategoryBreadcrumbs,
        loadGroupItemTemplateCategory,
        createGroupItemTemplateCategory,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingGroupItemTemplateCategory,
        creatingGroupItemTemplateCategory,
        createGroupItemTemplateCategorySuccess,
        GroupItemCategoryCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadGroupItemTemplateCategoryFailure) {
            navigate(`/error/${loadGroupItemTemplateCategoryErrorCode}`);
            clearGroupItemTemplateCategory();
        }
    }, [
        loadGroupItemTemplateCategoryFailure,
        loadGroupItemTemplateCategoryErrorCode,
        navigate,
        clearGroupItemTemplateCategory,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            groupItemTemplateCategoryIdNumber &&
            groupItemTemplateCategory?.value
        ) {
            setBreadcrumbs(
                GroupItemCategoryBreadcrumbs(
                    groupItemTemplateCategoryIdNumber,
                    groupItemTemplateCategory?.value
                )
            );
        }
    }, [
        groupItemTemplateCategoryIdNumber,
        groupItemTemplateCategory,
        setBreadcrumbs,
        GroupItemCategoryBreadcrumbs,
    ]);

    // Reload group item template category after saving
    useEffect(() => {
        if (
            !savingGroupItemTemplateCategory &&
            saveGroupItemTemplateCategorySuccess
        ) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Group Item Template Category saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/group/itemcategories";
                navigate(url);
            } else if (!groupItemTemplateCategoryIdNumber) {
                let url: string = `/settings/group/itemcategories/${groupItemTemplateCategory?.id}`;
                navigate(url);
                clearGroupItemTemplateCategory();
            }
        }
    }, [
        savingGroupItemTemplateCategory,
        saveGroupItemTemplateCategorySuccess,
        groupItemTemplateCategoryIdNumber,
        navigate,
        clearGroupItemTemplateCategory,
        saveAndReturn,
        groupItemTemplateCategory,
    ]);

    // Failed saving
    useEffect(() => {
        if (
            !savingGroupItemTemplateCategory &&
            saveGroupItemTemplateCategoryFailure
        ) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingGroupItemTemplateCategory, saveGroupItemTemplateCategoryFailure]);

    // Cleanup effect to clear the group item template category on component unmount
    useEffect(() => {
        return () => {
            clearGroupItemTemplateCategory();
            resetForm(reduxFormName);
        };
    }, [clearGroupItemTemplateCategory, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/group/itemcategories";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.GroupItemTemplateCategory) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.GroupItemTemplateCategory =
                {} as Dtos.GroupItemTemplateCategory;

            await saveGroupItemTemplateCategory(
                Object.assign({ ...data }, changes)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.GroupItemTemplateCategory =
                {} as Dtos.GroupItemTemplateCategory;

            if (
                form &&
                hasBeenSubmitted &&
                !validatingGroupItemTemplateCategory
            ) {
                await validateGroupItemTemplateCategory(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.GroupItemCategoriesUpdate,
                Dtos.Permission.GroupItemCategoriesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingGroupItemTemplateCategory ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "groupItemTemplateCategory",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "groupItemTemplateCategory",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "groupItemTemplateCategory",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "groupItemTemplateCategory",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingGroupItemTemplateCategory,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <GroupSettingsLayout
            loading={loadingGroupItemTemplateCategory}
            permission={Dtos.Permission.GroupItemCategoriesView}
        >
            {renderContent}
        </GroupSettingsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<GroupItemTemplateCategoryPageProps> => {
    let groupItemTemplateCategory: Dtos.GroupItemTemplateCategory | undefined =
        !(state.groupItemTemplateCategorys.formData instanceof Array)
            ? state.groupItemTemplateCategorys.formData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        groupItemTemplateCategory: groupItemTemplateCategory!,
        loadingGroupItemTemplateCategory:
            state.groupItemTemplateCategorys.loadState &&
            state.groupItemTemplateCategorys.loadState.status ==
                RequestState.Pending,
        loadGroupItemTemplateCategoryFailure:
            state.groupItemTemplateCategorys.loadState &&
            state.groupItemTemplateCategorys.loadState.status ==
                RequestState.Failure,
        loadGroupItemTemplateCategoryErrorCode:
            state.groupItemTemplateCategorys.loadState.errorCode,
        savingGroupItemTemplateCategory:
            state.groupItemTemplateCategorys.saveState &&
            state.groupItemTemplateCategorys.saveState.status ==
                RequestState.Pending,
        saveGroupItemTemplateCategorySuccess:
            state.groupItemTemplateCategorys.saveState &&
            state.groupItemTemplateCategorys.saveState.status ==
                RequestState.Success,
        saveGroupItemTemplateCategoryFailure:
            state.groupItemTemplateCategorys.saveState &&
            state.groupItemTemplateCategorys.saveState.status ==
                RequestState.Failure,
        creatingGroupItemTemplateCategory:
            state.groupItemTemplateCategorys.createState &&
            state.groupItemTemplateCategorys.createState.status ==
                RequestState.Pending,
        createGroupItemTemplateCategorySuccess:
            state.groupItemTemplateCategorys.createState &&
            state.groupItemTemplateCategorys.createState.status ==
                RequestState.Success,
        validatingGroupItemTemplateCategory:
            state.groupItemTemplateCategorys.validateState &&
            state.groupItemTemplateCategorys.validateState.status ==
                RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.groupItemTemplateCategory,
        formState: state.groupItemTemplateCategorys.formState,
        formProperties: state.groupItemTemplateCategorys.formProperties,
        validationFailures: groupItemTemplateCategory
            ? state.groupItemTemplateCategorys.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<GroupItemTemplateCategoryPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadGroupItemTemplateCategory: bindActionCreators(
            GroupItemTemplateCategoryActions.LoadGroupItemTemplateCategoryById,
            dispatch
        ),
        clearGroupItemTemplateCategory: bindActionCreators(
            GroupItemTemplateCategoryActions.Clear,
            dispatch
        ),
        saveGroupItemTemplateCategory: bindActionCreators(
            GroupItemTemplateCategoryActions.SaveGroupItemTemplateCategory,
            dispatch
        ),
        createGroupItemTemplateCategory: bindActionCreators(
            GroupItemTemplateCategoryActions.CreateGroupItemTemplateCategory,
            dispatch
        ),
        validateGroupItemTemplateCategory: bindActionCreators(
            GroupItemTemplateCategoryActions.ValidateGroupItemTemplateCategory,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(GroupItemTemplateCategoryPage);
